import { ACTION_DEFS, FIXED_KEYS, GROUP_LABEL, actionUsing, keyLabel, type ActionGroup } from '../input/actionDefs';
import { DEFAULT_BINDINGS, saveBindings, type ActionBinding, type Bindings } from '../input/bindings';

// D127 — the controls screen. The rebinding substrate has shipped since M2 (§13.2/§13.3, T-CLI-004);
// the only thing missing was a way to use it. Everything here is textContent-only DOM (T-SEC-006) and
// reads `ACTION_DEFS`, so a new action appears simply by being added to that table.
//
// Capture rules: click a row → "press a key…"; Escape cancels; Delete/Backspace unbinds an optional
// action; a key already in use SWAPS with its current owner (rather than silently stealing it, which
// leaves you with an unplayable control set and no idea which row broke).

export interface ControlsScreenOpts {
  readonly doc: Document;
  readonly storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
  readonly bindings: Bindings;
  /** Called whenever the bindings change, so the live game picks them up without a reload. */
  readonly onChange: (b: Bindings) => void;
}

export class ControlsScreen {
  private readonly root: HTMLDivElement;
  private readonly rows = new Map<ActionBinding, HTMLButtonElement>();
  private bindings: Bindings;
  private capturing: ActionBinding | null = null;
  private open = false;

  constructor(private readonly opts: ControlsScreenOpts) {
    this.bindings = { ...opts.bindings };
    const d = opts.doc;
    this.root = d.createElement('div');
    this.root.style.cssText =
      'position:fixed;inset:0;display:none;flex-direction:column;align-items:center;gap:12px;overflow:auto;' +
      'background:rgba(6,14,10,.97);color:#eaffea;font:600 13px ui-monospace,monospace;z-index:220;padding:24px';

    const title = d.createElement('div');
    title.textContent = 'Controls';
    title.style.cssText = 'font-size:20px;margin-bottom:4px';
    this.root.append(title);

    const hint = d.createElement('div');
    hint.textContent = 'Click a control, then press a key. Esc cancels · Delete clears an optional one.';
    hint.style.cssText = 'opacity:.75;margin-bottom:8px';
    this.root.append(hint);

    const groups = ['move', 'universal', 'defend', 'keeper', 'meta'] as const; // D137: the user's taxonomy
    for (const g of groups) this.root.append(this.groupBlock(d, g));

    const fixed = d.createElement('div');
    fixed.style.cssText = 'opacity:.6;margin-top:4px';
    fixed.textContent = `Fixed: ${FIXED_KEYS.map((f) => `${f.label} — ${keyLabel(f.code)}`).join(' · ')}`;
    this.root.append(fixed);

    const buttons = d.createElement('div');
    buttons.style.cssText = 'display:flex;gap:12px;margin-top:10px';
    buttons.append(this.button(d, 'Reset to defaults', () => this.reset()), this.button(d, 'Done', () => this.hide()));
    this.root.append(buttons);

    d.body.appendChild(this.root);
    d.addEventListener('keydown', this.onKeyDown, true);
    d.addEventListener('mousedown', this.onMouseDown, true);
  }

  private button(d: Document, label: string, onClick: () => void): HTMLButtonElement {
    const b = d.createElement('button');
    b.textContent = label;
    b.style.cssText = 'padding:8px 16px;border:1px solid #6f6;background:#163;color:#e8ffe8;border-radius:6px;cursor:pointer';
    b.onclick = onClick;
    return b;
  }

  private groupBlock(d: Document, group: ActionGroup): HTMLDivElement {
    const block = d.createElement('div');
    block.style.cssText = 'width:min(520px,90vw)';
    const h = d.createElement('div');
    h.textContent = GROUP_LABEL[group];
    h.style.cssText = 'opacity:.7;margin:10px 0 4px;text-transform:uppercase;letter-spacing:.08em;font-size:11px';
    block.append(h);
    for (const def of ACTION_DEFS.filter((a) => a.group === group)) {
      const row = d.createElement('div');
      row.style.cssText = 'display:flex;justify-content:space-between;align-items:center;padding:3px 0';
      const name = d.createElement('span');
      name.textContent = def.label;
      const key = d.createElement('button');
      key.style.cssText = 'min-width:132px;padding:5px 10px;border:1px solid #4a6;background:#0d2016;color:#cfc;border-radius:5px;cursor:pointer';
      key.onclick = () => this.beginCapture(def.id);
      this.rows.set(def.id, key);
      row.append(name, key);
      block.append(row);
    }
    return block;
  }

  private beginCapture(id: ActionBinding): void {
    this.capturing = id;
    this.render();
  }

  /** Apply a captured code, swapping with whoever held it. Returns the new binding set. */
  private assign(id: ActionBinding, code: string): void {
    const clash = actionUsing(this.bindings, code, id);
    const previous = this.bindings[id];
    const next: Bindings = { ...this.bindings, [id]: code };
    // D127: conflict = SWAP. Stealing silently leaves an action unbound with nothing on screen saying so.
    if (clash) next[clash] = previous;
    this.bindings = next;
    this.capturing = null;
    saveBindings(this.opts.storage, this.bindings);
    this.opts.onChange(this.bindings);
    this.render();
  }

  private reset(): void {
    this.bindings = { ...DEFAULT_BINDINGS };
    this.capturing = null;
    saveBindings(this.opts.storage, this.bindings);
    this.opts.onChange(this.bindings);
    this.render();
  }

  private readonly onKeyDown = (e: KeyboardEvent): void => {
    if (!this.open) return;
    if (this.capturing === null) {
      if (e.code === 'Escape') {
        e.preventDefault();
        this.hide();
      }
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    const id = this.capturing;
    if (e.code === 'Escape') {
      this.capturing = null;
      this.render();
      return;
    }
    if (e.code === 'Delete' || e.code === 'Backspace') {
      const def = ACTION_DEFS.find((a) => a.id === id)!;
      if (def.optional) this.assign(id, '');
      else {
        this.capturing = null;
        this.render();
      }
      return;
    }
    this.assign(id, e.code);
  };

  private readonly onMouseDown = (e: MouseEvent): void => {
    if (!this.open || this.capturing === null) return;
    // a click on the row that STARTED the capture is the click that opened it — ignore it
    if (this.rows.get(this.capturing) === e.target) return;
    e.preventDefault();
    e.stopPropagation();
    this.assign(this.capturing, `Mouse${e.button}`);
  };

  private render(): void {
    for (const def of ACTION_DEFS) {
      const el = this.rows.get(def.id);
      if (!el) continue;
      el.textContent = this.capturing === def.id ? 'press a key…' : keyLabel(this.bindings[def.id]);
      el.style.borderColor = this.capturing === def.id ? '#ffd166' : '#4a6';
    }
  }

  current(): Bindings {
    return { ...this.bindings };
  }

  show(): void {
    this.open = true;
    this.capturing = null;
    this.root.style.display = 'flex';
    this.render();
  }

  hide(): void {
    this.open = false;
    this.capturing = null;
    this.root.style.display = 'none';
  }

  get isOpen(): boolean {
    return this.open;
  }
}
